import { format, formatDistanceToNow } from 'date-fns';

/**
 * South African date formatting utilities.
 * SA standard: DD/MM/YYYY (day-first).
 */

/** "17/03/2026" -- the SA-canonical day-first format. The default
 *  ``formatDate`` and ``formatDateShort`` both return this so any
 *  caller picking the "obvious" name lands on the project rule. */
export const formatDate = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
};

/** "17/03/2026" -- alias of ``formatDate``; kept for backwards
 *  compatibility with existing call sites that imported the explicit
 *  short variant. New code should use ``formatDate``. */
export const formatDateShort = formatDate;

/** "17 Mar 2026" -- abbreviated month form, useful for dense lists
 *  where the visual texture of a month abbreviation reads more
 *  naturally than three digit groups. Use sparingly; the SA standard
 *  is DD/MM/YYYY (see ``formatDate``). */
export const formatDateMedium = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'dd MMM yyyy');
  } catch {
    return dateStr;
  }
};

/** "17 March 2026" */
export const formatDateLong = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'dd MMMM yyyy');
  } catch {
    return dateStr;
  }
};

/** "Monday, 17 March 2026" */
export const formatDateFull = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'EEEE, dd MMMM yyyy');
  } catch {
    return dateStr;
  }
};

/** "17 Mar" (no year — for timelines/charts) */
export const formatDateCompact = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'dd MMM');
  } catch {
    return dateStr;
  }
};

/** "Mar 2026" (month + year — for grouping) */
export const formatMonthYear = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'MMM yyyy');
  } catch {
    return dateStr;
  }
};

/** "17 Mar 2026, 14:30" (date + time, 24h) */
export const formatDateTime = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'dd MMM yyyy, HH:mm');
  } catch {
    return dateStr;
  }
};

/**
 * "2 days ago" / "3 hours ago"
 *
 * The API returns timestamps in UTC. Modern responses include a `Z` (or
 * offset) suffix, but some older fields are still naive ISO strings
 * (no timezone). We treat naive strings as UTC so `formatDistanceToNow`
 * does not interpret them as local time and return incorrect offsets.
 */
export const formatRelative = (dateStr: string): string => {
  try {
    const hasTz = /[Zz]|[+-]\d{2}:?\d{2}$/.test(dateStr);
    const withTz = hasTz ? dateStr : `${dateStr}Z`;
    return formatDistanceToNow(new Date(withTz), { addSuffix: true });
  } catch {
    return dateStr;
  }
};

/** ISO date for form values and API: "2026-03-17" */
export const toISODate = (date: Date = new Date()): string => {
  return format(date, 'yyyy-MM-dd');
};
